import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

import type { JustCallDialerBridge, JustCallDialerState } from './justcallDialerBridge';
import { cn } from '../cn';
import usePortal from '../hooks/usePortal';
import Button from '../Input2/Button';
import { Communication, General } from '../StrokeIcons';

export interface JustCallDockPanelProps {
  bridge: JustCallDialerBridge;
  state: JustCallDialerState;
}

/**
 * The visible dock: a panel pinned to the bottom-right corner holding the ONE JustCall iframe for
 * the page, plus the pill it folds into. It collapses instead of unmounting — removing the iframe
 * would reload JustCall's dialer (dropping a live call), so it stays in the DOM and is only hidden.
 * JustCall's own controls (sign-in, mute, keypad, hang up) all live inside the iframe.
 */
export function JustCallDockPanel({ bridge, state }: JustCallDockPanelProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const target = usePortal('poseidon-justcall-dock');

  useEffect(() => {
    const host = hostRef.current;
    if (host == null) return;
    bridge.attach(host);
    return () => bridge.detach(host);
  }, [bridge]);

  return createPortal(
    // Above the floating dialer (z-9000) so the rep can reach JustCall's controls mid-call; real
    // modals (Popup, z-9998) still stack above both.
    <div
      className="pointer-events-none fixed right-2 bottom-2 z-9001 flex flex-col items-end gap-2"
      data-test="justcall-dock">
      <div
        className={cn(
          'pointer-events-auto flex flex-col overflow-hidden rounded-xl bg-white shadow-2xl ring-1 ring-black/10 dark:bg-gray-900 dark:ring-white/15',
          !state.expanded && 'hidden',
        )}
        data-test="justcall-dock-panel">
        <div className="flex items-center gap-2 border-b border-gray-100 py-1 pr-1 pl-3 dark:border-gray-800">
          <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">JustCall</span>
          <span className="flex-1 truncate text-xs text-gray-500 dark:text-gray-400">
            {state.loggedIn === false ? 'Sign in below to place calls' : ''}
          </span>
          <Button
            text
            size="sm"
            icon={<General.Minus />}
            tooltip="Minimize"
            aria-label="Minimize JustCall dialer"
            onClick={() => bridge.collapse()}
            data-test="justcall-dock-minimize"
          />
        </div>
        {/* JustCall's dialer is built for 365×610; the extension side panel can be narrower. */}
        <div
          ref={hostRef}
          className="h-[610px] max-h-[calc(100vh-5rem)] w-[min(365px,calc(100vw-1rem))]"
          data-test="justcall-dock-host"
        />
      </div>
      {!state.expanded && (
        <Button
          className="pointer-events-auto shadow-lg"
          color="white"
          icon={<Communication.Phone />}
          tooltip={state.loggedIn === false ? 'Sign in to JustCall' : 'Open the JustCall dialer'}
          onClick={() => bridge.expand()}
          data-test="justcall-dock-open">
          {state.loggedIn === false ? 'JustCall · Sign in' : 'JustCall'}
        </Button>
      )}
    </div>,
    target,
  );
}
